import { onAnimationEnd } from "@theme/utilities";

/**
 * <hardware-nav> — mobile-expandable nav with drag-to-dismiss.
 *
 * The toggle button drives `aria-expanded` which CSS uses to show/hide
 * the nav list via `grid-template-rows`. On mobile, a drag gesture
 * interpolates that value for a swipe-to-close feel.
 *
 */
class HardwareNav extends HTMLElement {
  #controller = new AbortController();
  #dismissController = null;
  #toggle = null;
  #drawer = null;
  #backdrop = null;
  #isDragging = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#toggle = this.querySelector("[aria-expanded]");
    this.#drawer = this.querySelector(".hw-nav__drawer");
    this.#backdrop = this.querySelector(".hw-nav__backdrop");
    if (!this.#toggle || !this.#drawer || !this.#backdrop) return;

    this.#toggle.addEventListener("click", this.#handleToggle, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#dismissController?.abort();
  }

  get #isExpanded() {
    return this.#toggle?.matches('[aria-expanded="true"]');
  }

  #open() {
    this.#toggle.setAttribute("aria-expanded", "true");
    this.#bindDismissListeners();
  }

  #close = () => {
    this.#dismissController?.abort();
    this.#dismissController = null;
    this.#clearInlineStyles();
    this.#toggle.setAttribute("aria-expanded", "false");
  };

  #bindDismissListeners() {
    this.#dismissController?.abort();
    this.#dismissController = new AbortController();
    const { signal } = this.#dismissController;

    this.#backdrop?.addEventListener("click", this.#close, { signal });
    document.addEventListener("keydown", this.#handleKeydown, { signal });
    this.#setupDragDismiss(signal);
  }

  #clearInlineStyles() {
    this.#drawer.style.removeProperty("grid-template-rows");
    this.#drawer.style.removeProperty("transition");
    this.#backdrop.style.removeProperty("opacity");
    this.#backdrop.style.removeProperty("transition");
  }

  #handleToggle = () => {
    this.#isExpanded ? this.#close() : this.#open();
  };

  #handleKeydown = (e) => {
    if (e.key === "Escape") {
      this.#close();
      this.#toggle.focus();
    }
  };

  /**
   * Drag-to-dismiss for the mobile expanded nav.
   * Dragging down interpolates grid-template-rows from 1fr toward 0fr.
   * Releasing past 50% or with enough fling velocity closes the drawer.
   */
  #setupDragDismiss(signal) {
    const THRESHOLD = 8; // px before drag activates
    const FLING = 0.4; // px/ms velocity to trigger close

    let startY, prevY, prevTime, dragHeight;

    // Prevent native drag on images inside the drawer
    this.#drawer.addEventListener("dragstart", (e) => e.preventDefault(), {
      signal,
    });

    // Swallow clicks that land at the end of a drag gesture
    this.#drawer.addEventListener(
      "click",
      (e) => {
        if (this.#isDragging) e.preventDefault();
      },
      { signal },
    );

    const onPointerDown = (e) => {
      startY = prevY = e.clientY;
      prevTime = e.timeStamp;
      this.#isDragging = false;
      dragHeight = this.#drawer.scrollHeight;

      document.addEventListener("pointermove", onPointerMove, { signal });
      document.addEventListener("pointerup", onPointerUp, {
        once: true,
        signal,
      });
    };

    const onPointerMove = (e) => {
      const dy = e.clientY - startY;

      if (!this.#isDragging) {
        if (dy < THRESHOLD) return;
        this.#isDragging = true;
        this.#drawer.style.transition = "none";
        this.#backdrop.style.transition = "none";
      }

      e.preventDefault();
      const fraction = Math.max(0, 1 - dy / dragHeight);
      this.#drawer.style.gridTemplateRows = `${fraction}fr`;
      this.#backdrop.style.opacity = fraction;
      prevY = e.clientY;
      prevTime = e.timeStamp;
    };

    const onPointerUp = async (e) => {
      document.removeEventListener("pointermove", onPointerMove);
      if (!this.#isDragging) return;

      const dy = e.clientY - startY;
      const dt = e.timeStamp - prevTime;
      const velocity = dt > 0 ? (e.clientY - prevY) / dt : 0;
      const shouldClose = 1 - dy / dragHeight < 0.5 || velocity > FLING;

      // Re-enable CSS transitions for the snap animation
      this.#drawer.style.removeProperty("transition");
      this.#drawer.style.gridTemplateRows = shouldClose ? "0fr" : "1fr";
      this.#backdrop.style.removeProperty("transition");
      this.#backdrop.style.opacity = shouldClose ? 0 : 1;

      await onAnimationEnd(this.#drawer);
      this.#clearInlineStyles();
      if (shouldClose) this.#close();
    };

    document.addEventListener("pointerdown", onPointerDown, { signal });
  }
}

if (!customElements.get("hardware-nav")) {
  customElements.define("hardware-nav", HardwareNav);
}
